import React, { Component } from "react";
import { withRouter } from "react-router-dom";
import PropTypes from "prop-types";
import withStyles from "@material-ui/core/styles/withStyles";
import CircularProgress from "@material-ui/core/CircularProgress";
import Dialog from "@material-ui/core/Dialog";
import DialogContent from "@material-ui/core/DialogContent";
import DialogFooter from "components/Dialog/DialogFooter.jsx";
import MenuItem from "@material-ui/core/MenuItem";
import Select from "@material-ui/core/Select";
import InputLabel from "@material-ui/core/InputLabel";
import FormControl from "@material-ui/core/FormControl";
import Icon from "@material-ui/core/Icon";

import GridContainer from "components/Grid/GridContainer.jsx";
import GridItem from "components/Grid/GridItem.jsx";
import Card from "components/Card/Card.jsx";
import CardHeader from "components/Card/CardHeader.jsx";
import CardBody from "components/Card/CardBody.jsx";
import Button from "components/CustomButtons/Button.jsx";
import CustomInput from "components/CustomInput/CustomInput.jsx";
import DialogHeader from "components/Dialog/DialogHeader.jsx";
import DataTable, { Pill } from "components/DataTable/DataTable.jsx";

import api from "api/index";

const EVAL_STATUSES = ["draft", "submitted", "approved"];

const STATUS_TONES = {
  draft: "muted",
  submitted: "warning",
  approved: "success"
};

const BLANK_FORM = {
  staffId: "",
  period: "",
  score: "",
  notes: "",
  status: "draft"
};

const styles = {
  header: { display: "flex", justifyContent: "space-between", alignItems: "center" },
  formControl: { width: "100%", marginTop: "16px" },
  errorMsg: { color: "#f44336", fontSize: "13px" },
  infoMsg: { color: "#1565c0", fontSize: "12px", background: "#e3f2fd", borderRadius: "4px", padding: "8px 12px", marginBottom: "12px" },
  scoreBar: { display: "inline-block", height: "8px", borderRadius: "4px", background: "#1a237e", verticalAlign: "middle", marginRight: "6px" }
};

class StaffEvaluation extends Component {
  state = {
    evaluations: [],
    staff: [],
    loading: true,
    open: false,
    editing: null,
    form: { ...BLANK_FORM },
    saving: false,
    error: null,
    formError: null,
    apiUnavailable: false
  };

  componentDidMount() {
    this.fetchStaff();
    this.fetchEvaluations();
  }

  fetchStaff = async () => {
    try {
      const res = await api.get("/staff");
      const d = res.data.data || res.data;
      this.setState({ staff: Array.isArray(d) ? d : d.data || [] });
    } catch (e) { /* silent */ }
  };

  fetchEvaluations = async () => {
    this.setState({ loading: true, error: null });
    try {
      const res = await api.get("/hr/evaluations");
      const d = res.data.data || res.data;
      this.setState({ evaluations: Array.isArray(d) ? d : d.data || [], loading: false, apiUnavailable: false });
    } catch (e) {
      this.setState({ evaluations: [], loading: false, apiUnavailable: true });
    }
  };

  handleChange = field => e => {
    const v = e.target.value;
    this.setState(prev => ({ form: { ...prev.form, [field]: v } }));
  };

  openAdd = () => {
    this.setState({ open: true, editing: null, form: { ...BLANK_FORM }, formError: null });
  };

  openEdit = eval_ => {
    this.setState({
      open: true,
      editing: eval_.id,
      formError: null,
      form: {
        staffId: eval_.staffId ? String(eval_.staffId) : "",
        period: eval_.period || "",
        score: eval_.score != null ? String(eval_.score) : "",
        notes: eval_.notes || "",
        status: eval_.status || "draft"
      }
    });
  };

  handleSave = async () => {
    const { form, editing, apiUnavailable } = this.state;
    if (!form.staffId || !form.period) {
      this.setState({ formError: "Staff member and evaluation period are required." });
      return;
    }
    if (form.score !== "" && (isNaN(Number(form.score)) || Number(form.score) < 0 || Number(form.score) > 100)) {
      this.setState({ formError: "Score must be a number between 0 and 100." });
      return;
    }
    this.setState({ saving: true, formError: null });

    const payload = { ...form, score: form.score !== "" ? Number(form.score) : null };

    if (apiUnavailable) {
      // Local state fallback
      if (editing) {
        this.setState(prev => ({
          evaluations: prev.evaluations.map(e => e.id === editing ? { ...e, ...payload } : e),
          open: false, saving: false
        }));
      } else {
        const staffMember = this.state.staff.find(s => String(s.id) === form.staffId);
        const newEval = {
          ...payload,
          id: Date.now(),
          staffName: staffMember ? `${staffMember.firstName || ""} ${staffMember.lastName || ""}`.trim() : "Unknown"
        };
        this.setState(prev => ({ evaluations: [newEval, ...prev.evaluations], open: false, saving: false }));
      }
      return;
    }

    try {
      if (editing) {
        await api.put(`/hr/evaluations/${editing}`, payload);
      } else {
        await api.post("/hr/evaluations", payload);
      }
      this.setState({ open: false, saving: false });
      this.fetchEvaluations();
    } catch (e) {
      // Silently degrade to local state
      const staffMember = this.state.staff.find(s => String(s.id) === form.staffId);
      const staffName = staffMember ? `${staffMember.firstName || ""} ${staffMember.lastName || ""}`.trim() : "Unknown";
      if (editing) {
        this.setState(prev => ({
          evaluations: prev.evaluations.map(ev => ev.id === editing ? { ...ev, ...payload } : ev),
          open: false, saving: false, apiUnavailable: true
        }));
      } else {
        this.setState(prev => ({
          evaluations: [{ ...payload, id: Date.now(), staffName }, ...prev.evaluations],
          open: false, saving: false, apiUnavailable: true
        }));
      }
    }
  };

  getStaffName = (eval_) => {
    if (eval_.staffName) return eval_.staffName;
    if (eval_.staff) return `${eval_.staff.firstName || ""} ${eval_.staff.lastName || ""}`.trim();
    const s = this.state.staff.find(x => String(x.id) === String(eval_.staffId));
    return s ? `${s.firstName || ""} ${s.lastName || ""}`.trim() : eval_.staffId || "—";
  };

  render() {
    const { classes } = this.props;
    const { evaluations, staff, loading, open, editing, form, saving, error, formError, apiUnavailable } = this.state;

    const columns = [
      { id: "staffName", header: "Staff Name", accessorFn: ev => this.getStaffName(ev) },
      { id: "period", header: "Period", accessorFn: ev => ev.period || "—" },
      {
        id: "score",
        header: "Score",
        cell: info => {
          const ev = info.row.original;
          const scorePct = ev.score != null ? Math.min(100, Math.max(0, ev.score)) : null;
          if (scorePct == null) return "—";
          return (
            <span>
              <span className={classes.scoreBar} style={{ width: `${scorePct}px`, maxWidth: "60px" }} />
              {scorePct}/100
            </span>
          );
        }
      },
      { id: "evaluator", header: "Evaluator", accessorFn: ev => ev.evaluator || ev.evaluatorName || "—" },
      {
        id: "status",
        header: "Status",
        cell: info => {
          const status = info.row.original.status || "draft";
          return <Pill tone={STATUS_TONES[status] || "muted"}>{status}</Pill>;
        }
      },
      {
        id: "actions",
        header: "",
        cell: info => (
          <Button size="sm" color="info" simple onClick={() => this.openEdit(info.row.original)}>Edit</Button>
        )
      }
    ];

    return (
      <GridContainer>
        <GridItem xs={12}>
          <Card>
            <CardHeader color="primary" style={{ margin: -8, padding: "24px 20px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div>
                  <h4 style={{ margin: 0, color: "#fff", fontWeight: "bold" }}>Staff Evaluations</h4>
                  <p style={{ margin: "2px 0 0", color: "rgba(255,255,255,0.8)", fontSize: "13px" }}>
                    {evaluations.length} evaluation{evaluations.length !== 1 ? "s" : ""}
                  </p>
                </div>
                <div style={{ display: "flex", gap: "8px" }}>
                  <Button size="sm" color="white" onClick={this.openAdd}>
                    <Icon style={{ marginRight: "4px", fontSize: "16px" }}>add</Icon> Add Evaluation
                  </Button>
                </div>
            </CardHeader>
            <CardBody>
              {apiUnavailable && (
                <div className={classes.infoMsg}>
                  <Icon style={{ fontSize: "14px", verticalAlign: "middle", marginRight: "6px" }}>info</Icon>
                  Evaluations API not available — data is stored locally for this session only.
                </div>
              )}
              {error && <p className={classes.errorMsg}>{error}</p>}

              <DataTable
                columns={columns}
                data={evaluations}
                isLoading={loading}
                emptyState="No evaluations found. Add one to get started."
              />
            </CardBody>
          </Card>
        </GridItem>

        <Dialog open={open} onClose={() => this.setState({ open: false })} maxWidth="sm" fullWidth>
          <DialogHeader title={editing ? "Edit Evaluation" : "Add Staff Evaluation"} />
          <DialogContent>
            {formError && <p className={classes.errorMsg}>{formError}</p>}
            <GridContainer>
              <GridItem xs={12} sm={6}>
                <FormControl className={classes.formControl}>
                  <InputLabel>Staff Member *</InputLabel>
                  <Select value={form.staffId} onChange={this.handleChange("staffId")}>
                    <MenuItem value="">— Select Staff —</MenuItem>
                    {staff.map(s => (
                      <MenuItem key={s.id} value={String(s.id)}>
                        {`${s.firstName || ""} ${s.lastName || ""}`.trim() || s.staffNo}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </GridItem>
              <GridItem xs={12} sm={6}>
                <CustomInput
                  labelText="Evaluation Period *"
                  id="evPeriod"
                  formControlProps={{ fullWidth: true }}
                  inputProps={{ value: form.period, onChange: this.handleChange("period"), placeholder: "e.g. Term 1 2025" }}
                />
              </GridItem>
              <GridItem xs={12} sm={4}>
                <CustomInput
                  labelText="Score (0-100)"
                  id="evScore"
                  formControlProps={{ fullWidth: true }}
                  inputProps={{ value: form.score, onChange: this.handleChange("score"), type: "number", min: "0", max: "100" }}
                />
              </GridItem>
              <GridItem xs={12} sm={4}>
                <FormControl className={classes.formControl}>
                  <InputLabel>Status</InputLabel>
                  <Select value={form.status} onChange={this.handleChange("status")}>
                    {EVAL_STATUSES.map(s => (
                      <MenuItem key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </GridItem>
              <GridItem xs={12}>
                <CustomInput
                  labelText="Notes"
                  id="evNotes"
                  formControlProps={{ fullWidth: true }}
                  inputProps={{
                    value: form.notes,
                    onChange: this.handleChange("notes"),
                    multiline: true,
                    rows: 3,
                    placeholder: "Optional notes..."
                  }}
                />
              </GridItem>
            </GridContainer>
          </DialogContent>
          <DialogFooter>
            <Button outlined onClick={() => this.setState({ open: false })} disabled={saving}>Cancel</Button>
            <Button color="primary" onClick={this.handleSave} disabled={saving}>
              {saving ? <CircularProgress size={18} style={{ color: "#fff" }} /> : (editing ? "Update" : "Add Evaluation")}
            </Button>
          </DialogFooter>
        </Dialog>
      </GridContainer>
    );
  }
}

StaffEvaluation.propTypes = { classes: PropTypes.object.isRequired };
export default withRouter(withStyles(styles)(StaffEvaluation));
